import { tv } from '@simplywallst/ui-core/next';
import { useEffect, useId, useRef, useState } from 'react';
import { parseNumberSafe, processPositiveNumberInput } from '../../../../utils';
import { isInputValid } from './utils';
import {
  stripFractionsExceedingDecimals,
  roundNumber,
} from '@/pages/Narrative/utils/number';
import { FAIR_VALUE_PRECISION } from '@/pages/Narrative/utils/schema';

export const field = tv({
  slots: {
    fieldset: 'group/field m-0 grid gap-x0_25',
    label:
      'm-0 text-tiny text-solid opacity-70 transition-opacity group-focus-within/field:opacity-100',
    inputEl:
      'w-full select-none bg-transparent p-0 text-lg font-strong text-[--s-field-input-color-text] caret-brand-01',
    input: [
      '[.s-focus-kb_&]:focus-within:outline-dotted [.s-focus-kb_&]:focus-within:outline-2 [.s-focus-kb_&]:focus-within:outline-offset-2 [.s-focus-kb_&]:focus-within:outline-[--s-focus-color]',
      'group/input relative rounded-md text-lg font-strong',
      'border border-solid border-[--s-field-input-color-border]',
      'bg-[--s-field-input-color-bg]',
      'opacity-70 transition-opacity',
      'group-focus-within/field:bg-[--s-field-input-focus-color-bg] group-focus-within/field:opacity-100',
      'grid grid-cols-[max-content_1fr] items-center justify-center gap-x0_5 px-x1',
    ],
    inputPrefix: [
      'pointer-events-none start-x1_25 grid items-center',
      'fill-[--s-field-input-color-text] text-tiny font-strong opacity-70 transition-opacity group-focus-within/field:opacity-100',
    ],
  },
  variants: {
    hasError: {
      true: {
        input: 'border-[--s-field-input-error-color-border]',
      },
    },
  },
  defaultVariants: {
    hasError: false,
  },
});

type Props = {
  value: number;
  onChange: (value: number) => void;
  currencySymbol: string;
};

export const NarrativeMiniValuatorFairValueInput = ({
  value,
  onChange,
  currencySymbol,
}: Props) => {
  const [input, setInput] = useState('');
  const inputNumber = parseNumberSafe(input, FAIR_VALUE_PRECISION);
  const isValid = isInputValid(input);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInput = (currentValue: string | undefined) => {
    const newValue = stripFractionsExceedingDecimals(
      processPositiveNumberInput(currentValue),
      FAIR_VALUE_PRECISION
    );

    setInput(newValue);

    // Only emit changed value if doesn't differ from converted number ingested through useEffect below
    if (newValue === Number(newValue).toString() && isInputValid(newValue)) {
      onChange(parseNumberSafe(newValue, FAIR_VALUE_PRECISION));
    }
  };

  const handleBlur = () => {
    if (!isValid) {
      return;
    }

    onChange(inputNumber);
  };

  useEffect(() => {
    setInput(roundNumber(value, FAIR_VALUE_PRECISION).toString());
  }, [value]);

  const id = useId();

  const styles = field({
    hasError: !isValid,
  });

  return (
    <fieldset className={styles.fieldset()}>
      <label className={styles.label()} htmlFor={id}>
        Your Fair Value
      </label>
      <div className={styles.input()} onClick={() => inputRef.current?.focus()}>
        <span className={styles.inputPrefix()}>{currencySymbol}</span>
        <input
          id={id}
          ref={inputRef}
          className={styles.inputEl()}
          name="fairValue"
          aria-invalid={!isValid}
          type="tel"
          autoComplete="off"
          value={input}
          onChange={(e) => handleInput(e.currentTarget.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          inputMode="decimal"
          pattern="[0-9.]*"
        />
      </div>
    </fieldset>
  );
};
